import { useCallback, useEffect, useState } from 'react';

import * as yup from 'yup';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import { FormikProvider, useFormik } from 'formik';

import i18n from '../../../../../../i18n';
import { InputField } from '../../../../../InputField/InputField';
import { useCheckout } from '../../../../../../hooks/use-checkout';
import { getLocale } from '../../../../../../utils/get-locale-util';
import { useNuveiSubmit } from '../../../../hooks/use-nuvei-submit';
import { usePaymentState } from '../../../../hooks/use-payment-state';
import { useAppConfig } from '../../../../../../hooks/use-app-config';
import { AddressElement } from '../../../AddressElement/AddressElement';
import { CheckboxField } from '../../../../../CheckboxField/CheckboxField';
import { PayButton } from '../../../PaymentForm/components/PayButton/PayButton';
import { EMAIL_FIELD_DEFAULT_ATTRIBUTES } from '../../../../../../constants/consts';
import { BackToSavedCardButton } from '../../../BackToSavedCardButton/BackToSavedCardButton';
import { useAddressElementRef } from '../../../AddressElement/hooks/use-address-element-ref';
import { useShowTaxCalculationFields } from '../../../../hooks/use-show-tax-calculation-fields';
import { PaymentElementContainer } from '../../../PaymentElementContainer/PaymentElementContainer';

import './styles.scss';

export const getCardFormSchema = (shouldHideEmailField?: boolean) => {
  return yup.object().shape({
    email: shouldHideEmailField
      ? yup.string().notRequired()
      : yup
          .string()
          .required(i18n.t('checkout.fieldErrors.incompleteEmail'))
          .email(i18n.t('checkout.fieldErrors.invalidEmail')),
    cardholderName: yup.string().required(i18n.t('checkout.fieldErrors.incompleteCardholderName')),
  });
};

interface NuveiCardProps {
  onReady: () => void;
  savedCardProps: {
    defaultSaveCardChecked: boolean;
    onBackButtonClicked: () => void;
    showBackButton: boolean;
  };
}

export interface NuveiCardForm {
  email: string;
  cardholderName: string;
  shouldSaveCard: boolean;
}

export function NuveiCard({ onReady, savedCardProps }: NuveiCardProps) {
  const [cardObj, setCardObj] = useState<any>(null);
  const [CVCError, setCVCError] = useState({ message: '', isValid: false });
  const [cardError, setCardError] = useState({ message: '', isValid: false });
  const [expirationError, setExpirationError] = useState({ message: '', isValid: false });
  const { safeCharge, checkoutData } = useCheckout();
  const { handleCardPay } = useNuveiSubmit();
  const isPaymentLoading = usePaymentState((state) => state.isPaymentLoading);
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
  const { showAddressField, useTaxCalculationField } = useShowTaxCalculationFields();
  const { AddressElementRef } = useAddressElementRef();
  const [loading, setLoading] = useState({
    cardNumber: true,
    securityCode: true,
    expiration: true,
  });

  const { featureFlags } = useAppConfig();
  const shouldHideEmailField = featureFlags?.checkout_enforce_customer_email;
  const shouldHideFieldsLabels = featureFlags?.checkout_remove_field_labels;

  const isFieldsReady = Object.values(loading).every((value) => !value);
  const showSaveMyCard = Boolean(checkoutData?.checkoutConfigurations?.showSaveMyCard);
  const isCardFieldsValid = cardError.isValid && CVCError.isValid && expirationError.isValid;

  const formik = useFormik<NuveiCardForm>({
    initialValues: {
      email: checkoutData?.customer?.email || '',
      cardholderName: '',
      shouldSaveCard:
        showSaveMyCard &&
        (savedCardProps.defaultSaveCardChecked ||
          Boolean(featureFlags?.checkout_select_save_card_by_default)),
    },
    validateOnMount: true,
    enableReinitialize: true,
    validationSchema: getCardFormSchema(shouldHideEmailField),
    onSubmit: (values) => console.log({ values }),
  });

  const getErrorMessage = useCallback(
    (message?: string) => (message ? t(`fieldErrors.${message}`) : ''),
    []
  );

  const onPayClicked = () => {
    formik.submitForm();
    handleCardPay(
      formik.isValid && isCardFieldsValid,
      formik.values,
      cardObj,
      AddressElementRef.current
    );
  };

  useEffect(() => {
    if (safeCharge) {
      onReady();
      const safeChargeFields = safeCharge.fields({
        locale: getLocale().pspLocale,
        fonts: [
          {
            cssUrl:
              'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;800&family=Open+Sans:wght@600&family=Oswald:wght@400;600&family=Roboto:wght@400;500;700&display=swap',
          },
        ],
      });
      // Card number----------------------------------------------------
      const cardNumber = safeChargeFields.create('ccNumber', {
        style: {
          base: {
            fontWeight: 'lighter',
            color: '#30313d',
            fontSize: '16px',
          },
        },
      });
      setCardObj(cardNumber);
      cardNumber.attach('#card-number');
      cardNumber.on('change', (evt: any) => {
        // console.log('cardNumber change event >>>', evt);
        if (evt.error) {
          const errorMessage = getErrorMessage(evt.error?.message);
          setCardError((prev) => ({
            ...prev,
            message: errorMessage,
            isValid: false,
          }));
        } else {
          setCardError((prev) => ({ ...prev, message: '', isValid: true }));
          cardExpiry.focus();
        }
      });
      cardNumber.on('ready', () => {
        setLoading((prev) => ({ ...prev, cardNumber: false }));
      });
      // cardNumber.on('blur', (evt: any) => {
      //   // console.log('cardNumber blur event >>>', evt);
      //   setCardError((prev) => ({ ...prev, isTouched: true }));
      // });
      // Expartion--------------------------------------------------------------
      const cardExpiry = safeChargeFields.create('ccExpiration', {
        style: {
          base: {
            fontSize: '16px',
            color: '#30313d',
            fontWeight: 'lighter',
          },
        },
      });
      cardExpiry.attach('#card-expiry');
      cardExpiry.on('change', (evt: any) => {
        // console.log('cardExpiry change event >>>', evt);
        if (evt.error) {
          const errorMessage = getErrorMessage(evt.error?.message);
          setExpirationError({ message: errorMessage, isValid: false });
        } else {
          setExpirationError({ message: '', isValid: true });
          cardCvc.focus();
        }
      });
      cardExpiry.on('ready', () => {
        setLoading((prev) => ({ ...prev, expiration: false }));
      });
      // CVV--------------------------------------------------------------
      const cardCvc = safeChargeFields.create('ccCvc', {
        style: {
          base: {
            fontSize: '16px',
            color: '#30313d',
            fontWeight: 'lighter',
          },
        },
      });
      cardCvc.on('change', (evt: any) => {
        // console.log('cardCvc change event >>>', evt);
        if (evt.error) {
          const errorMessage = getErrorMessage(evt.error?.message);
          setCVCError({ message: errorMessage, isValid: false });
        } else {
          setCVCError({ message: '', isValid: true });
        }
      });
      cardCvc.on('ready', () => {
        setLoading((prev) => ({ ...prev, securityCode: false }));
      });
      cardCvc.attach('#card-cvc');
    }
  }, [safeCharge, setCardObj, onReady, getErrorMessage]);

  return (
    <PaymentElementContainer
      content={
        <FormikProvider value={formik}>
          <Stack spacing={1.5} style={{ pointerEvents: isPaymentLoading ? 'none' : 'auto' }}>
            {savedCardProps.showBackButton && (
              <BackToSavedCardButton onClick={savedCardProps.onBackButtonClicked} />
            )}
            <InputField
              name="cardholderName"
              label={shouldHideFieldsLabels ? '' : t('cardholderName')}
              inputProps={{
                placeholder: t('cardholderName'),
              }}
            />
            {!shouldHideEmailField && (
              <InputField
                name="email"
                label={shouldHideFieldsLabels ? '' : t('email')}
                inputProps={{
                  placeholder: t('emailAddress'),
                  ...EMAIL_FIELD_DEFAULT_ATTRIBUTES,
                }}
              />
            )}
            <NuveiInputField
              isLoading={!isFieldsReady}
              label={shouldHideFieldsLabels ? '' : t('cardNumber')}
              id="card-number"
              errorMessage={cardError.message}
            />
            <Stack direction="row" spacing={1.5}>
              <NuveiInputField
                isLoading={!isFieldsReady}
                label={shouldHideFieldsLabels ? '' : t('expiration')}
                id="card-expiry"
                errorMessage={expirationError.message}
              />
              <NuveiInputField
                isLoading={!isFieldsReady}
                label={shouldHideFieldsLabels ? '' : t('CVV')}
                id="card-cvc"
                errorMessage={CVCError.message}
              />
            </Stack>
            {showSaveMyCard && (
              <CheckboxField label={t('saveCardCheckbox')} name="shouldSaveCard" />
            )}

            {useTaxCalculationField && (
              <AddressElement ref={AddressElementRef} zipOnly={!showAddressField} />
            )}
          </Stack>
        </FormikProvider>
      }
    >
      <PayButton onClick={onPayClicked} />
    </PaymentElementContainer>
  );
}

interface NuveiInputFieldProps {
  label: string;
  id: string;
  errorMessage: string;
  isLoading: boolean;
}

function NuveiInputField({ label, id, errorMessage, isLoading }: NuveiInputFieldProps) {
  return (
    <Box flex={1}>
      {label && (
        <label className="s-label" htmlFor={id}>
          {label}
        </label>
      )}
      <div
        id={id}
        className={`s-input nuvei-input-field ${errorMessage ? 'invalid' : ''} ${
          isLoading ? 'loading' : ''
        }`}
      />
      {errorMessage && <p className="p-FieldError Error">{errorMessage}</p>}
    </Box>
  );
}
